/**
 * A price a person sets on a draft line (round 4 of the minimal redesign, 12 Oct 2026).
 *
 * The backend stores it on the line's override (`POST /cases/{id}/lines/{i}/override`,
 * kind "edit", the fields `unit_price`, `currency`, `price_note`) and, when the case is
 * read, puts it into the same line fields the engine's price uses, with
 * `price_source: "sales"`. So the preview, the checks, the reply and the F&O draft check
 * follow without knowing where the price came from. The agreement figure and the
 * autonomy gate never read it.
 *
 * The wire shapes are the generated `LineOverride`, `LineOverrideRequest` and
 * `QuotePreviewLine` (`price_source`, `price_set_by`, `price_set_at`); this file holds
 * only the input rules and the sentences. Decimals travel as strings both ways.
 */

import { personName } from "@/lib/api";
import { formatAmount, formatDay } from "@/lib/work-labels";

/** The highest price the backend accepts (le 1_000_000_000). */
const MAX_PRICE = 1_000_000_000;

/**
 * Why the dialog cannot save yet, or null when the price and the currency are fine. The
 * same rules as the backend's validation (gt 0, two decimals, three-letter currency), so
 * a refusal is read here before a request is made.
 */
export function priceInputError(price: string, currency: string): string | null {
  return priceAmountError(price) ?? currencyInputError(currency);
}

/** Why the price box cannot be saved yet, or null; the currency is read apart (round 5). */
export function priceAmountError(price: string): string | null {
  const typed = price.trim().replace(",", ".");
  if (typed === "") {
    return "Enter a price.";
  }
  const number = Number(typed);
  if (!/^\d*\.?\d*$/.test(typed) || !Number.isFinite(number) || number <= 0) {
    return "Enter a price above 0, like 412.50.";
  }
  const decimals = typed.split(".")[1] ?? "";
  if (decimals.length > 2) {
    return "Use at most two decimals.";
  }
  if (number > MAX_PRICE) {
    return "This price is too high.";
  }
  return null;
}

/**
 * Why the currency box cannot be saved yet, or null. Its own function so the dialog shows
 * the sentence under the Currency box, not under the price (tester round 5, 12 Oct 2026:
 * on a line without any price the box starts empty, and the sentence under the price box
 * read like a wrong price).
 */
export function currencyInputError(currency: string): string | null {
  return /^[A-Za-z]{3}$/.test(currency.trim())
    ? null
    : "Enter a three-letter currency code, like EUR.";
}

/** "412.50" as the backend wants it: a plain decimal string with two places. */
export function priceToWire(price: string): string {
  return Number(price.trim().replace(",", ".")).toFixed(2);
}

/** "Price set by Priya Shah on 12 Oct 2026" for a price a person set; empty otherwise. */
export function priceSetByText(price: {
  source?: "engine" | "sales" | null;
  setBy?: string | null;
  setAt?: string | null;
}): string {
  if (price.source !== "sales") {
    return "";
  }
  const who = personName(price.setBy) || "a colleague";
  const when = formatDay(price.setAt);
  return when ? `Price set by ${who} on ${when}` : `Price set by ${who}`;
}

/** "I2Q found 398.00 GBP", or "I2Q found no price" when the engine had none. */
export function engineFoundText(
  unitPrice: string | number | null | undefined,
  currency: string | null | undefined,
): string {
  const amount = formatAmount(unitPrice);
  return amount
    ? `I2Q found ${[amount, currency].filter(Boolean).join(" ")}`
    : "I2Q found no price";
}
